import React, { useState, useMemo, useEffect } from "react";
import Navbar from "../../components/Navbar";
import "./Timeline.css";
import {
    LayoutGrid as ThLarge,
    Folder,
    ClipboardList as Tasks,
    Calendar as CalendarIcon,
    Users,
    Plus,
    ChevronRight,
    Menu as Bars,
    Filter,
    ArrowDownWideNarrow as SortAmountDown,
    Search,
    Download,
    ChevronDown,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetTasksQuery, useGetProjectsQuery } from "../../services/rtkClient";

const Timeline = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isSidebarVisible, setIsSidebarVisible] = useState(false);
    const [projectsOpen, setProjectsOpen] = useState(false);
    const [statusOpen, setStatusOpen] = useState(false);
    const projectName = location.state?.projectName || sessionStorage.getItem('projectName') || "";
    const [currentProjectName, setCurrentProjectName] = useState(projectName || "New Workspace");
    const [filterCriteria, setFilterCriteria] = useState({ type: null, value: null });

    const getCurrentUserEmail = () => {
        try {
            const storedUser = localStorage.getItem("user");
            if (storedUser) {
                const user = JSON.parse(storedUser);
                return user.email || user.username || "default";
            }
        } catch (err) { }
        return "default";
    };

    const { data: serverTasksData } = useGetTasksQuery();
    const { data: serverProjectsData } = useGetProjectsQuery();

    const recentProjects = useMemo(() => {
        const raw = serverProjectsData?.data || serverProjectsData?.schema || (Array.isArray(serverProjectsData) ? serverProjectsData : []);
        return raw.map(p => ({
            id: p.id || p.projectId,
            name: p.name || p.projectName || "Unnamed Project"
        }));
    }, [serverProjectsData]);

    useEffect(() => {
        if (!location.state?.projectName && recentProjects.length > 0) {
            setCurrentProjectName(recentProjects[0].name);
        }
    }, [recentProjects, location.state?.projectName]);

    // Flatten tasks if they come in status buckets (like from TaskTools)
    const tasks = useMemo(() => {
        const rawTasks = serverTasksData?.data || serverTasksData?.schema || (Array.isArray(serverTasksData) ? serverTasksData : (location.state?.tasks || []));
        if (!rawTasks) return [];
        let all = [];
        if (Array.isArray(rawTasks)) {
            all = rawTasks;
        } else {
            all = Object.values(rawTasks).flat();
        }

        if (!filterCriteria.type || !filterCriteria.value) return all;

        return all.filter((task) => {
            if (filterCriteria.type === "Project") return true;
            if (filterCriteria.type === "Status") {
                const statusMap = {
                    "Completed": "Completed",
                    "In Progress": "In Progress",
                    "Hold": "On Hold"
                };
                return task.status === (statusMap[filterCriteria.value] || filterCriteria.value);
            }
            return true;
        });
    }, [location.state?.tasks, filterCriteria]);

    const handleSubItemClick = (type, value) => {
        if (type === "Project") {
            setCurrentProjectName(value);
            setFilterCriteria({ type: "Project", value });
        } else if (type === "Status") {
            if (value === "All") {
                setFilterCriteria({ type: null, value: null });
            } else {
                setFilterCriteria({ type: "Status", value });
            }
        }
    };

    const handleExport = () => {
        if (tasks.length === 0) {
            alert("No tasks to export");
            return;
        }

        const headers = ["Task", "Status", "Priority", "Assignee"];
        const csvRows = tasks.map(t => [
            `"${t.title || t.task || 'Untitled'}"`,
            t.status || 'Planned',
            t.priority || 'Medium',
            `"${t.assignee || 'Unassigned'}"`
        ].join(","));

        const csvContent = [headers.join(","), ...csvRows].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `timeline_${currentProjectName.replace(/\s+/g, '_').toLowerCase()}.csv`);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="timeline-wrapper">
            <Navbar />
            <div className="timeline-container">
                {/* Sidebar */}
                {isSidebarVisible && (
                    <aside className="lp-sidebar">
                        <div className="lp-sidebar-nav">
                            <div className="lp-nav-item active">
                                <span className="lp-nav-icon"><ThLarge size={18} /></span>
                                Dashboard
                            </div>



                        </div>


                        <div className="lp-sidebar-nav secondary">





                        </div>

                        <div className="lp-nav-section-header">
                            <span>New Project</span>
                            <span className="lp-section-add" onClick={() => navigate('/onboarding')} style={{ cursor: 'pointer' }}>
                                <Plus size={14} />
                            </span>
                        </div>
                        <div className="lp-sidebar-nav secondary">
                            <div className="lp-nav-item" onClick={() => setProjectsOpen(!projectsOpen)} style={{ cursor: "pointer" }}>
                                <span className="lp-nav-icon"><Folder size={18} /></span>
                                Projects
                                <span className="lp-nav-arrow">{projectsOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
                            </div>
                            {projectsOpen && (
                                <div className="lp-sub-items">
                                    {recentProjects.map((proj, idx) => (
                                        <div key={idx} className="lp-sub-item" onClick={() => handleSubItemClick("Project", proj.name)}>{proj.name}</div>
                                    ))}
                                </div>
                            )}

                            <div className="lp-nav-item" onClick={() => setStatusOpen(!statusOpen)} style={{ cursor: "pointer" }}>
                                <span className="lp-nav-icon"><Tasks size={18} /></span>
                                Status
                                <span className="lp-nav-arrow">{statusOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
                            </div>
                            {statusOpen && (
                                <div className="lp-sub-items">
                                    <div className="lp-sub-item" onClick={() => handleSubItemClick("Status", "All")}>All</div>
                                    <div className="lp-sub-item" onClick={() => handleSubItemClick("Status", "Completed")}>Completed</div>
                                    <div className="lp-sub-item" onClick={() => handleSubItemClick("Status", "In Progress")}>In Progress</div>
                                    <div className="lp-sub-item" onClick={() => handleSubItemClick("Status", "Hold")}>Hold</div>
                                </div>
                            )}
                        </div>

                        <div className="lp-sidebar-footer">
                            <button className="lp-invite-btn">
                                <Users size={16} /> Invite People
                            </button>
                        </div>
                    </aside>
                )}

                <main className="lp-main-content">
                    <header className="lp-header">
                        <div className="lp-header-top">
                            <button
                                className="sidebar-toggle-btn"
                                onClick={() => setIsSidebarVisible(!isSidebarVisible)}
                                title={isSidebarVisible ? "Hide Sidebar" : "Show Sidebar"}
                            >
                                <Bars size={18} />
                            </button>
                            <div className="lp-breadcrumb">
                                <Folder className="lp-breadcrumb-icon" size={14} /> {projectName}
                            </div>
                        </div>
                        <div className="lp-header-title">
                            <div className="lp-project-avatar" style={{ background: "linear-gradient(135deg, #0052cc, #6554c0)" }}>
                                <Tasks size={18} />
                            </div>
                            <h1>{currentProjectName}</h1>
                        </div>

                        <div className="lp-tabs">
                            <div className="lp-tab" onClick={() => navigate("/summary", { state: { tasks, projectName } })}>Summary</div>
                            <div className="lp-tab" onClick={() => navigate("/tasks", { state: { tasks, projectName } })}>Board</div>
                            <div className="lp-tab" onClick={() => navigate("/listpage", { state: { tasks, projectName } })}>Tasks List</div>
                            <div className="lp-tab active">Timeline</div>
                            <div className="lp-tab" onClick={() => navigate("/calendar", { state: { tasks, projectName } })}>Calendar</div>
                        </div>
                    </header>

                    <div className="lp-toolbar">
                        <div className="lp-toolbar-left">
                            <button className="lp-tool-btn"><Filter size={14} /> Filter</button>
                            <button className="lp-tool-btn"><SortAmountDown size={14} /> Sort</button>
                            <div className="lp-search-container">
                                <Search className="lp-search-icon" size={14} />
                                <input type="text" placeholder="Search timeline..." className="lp-search-input" />
                            </div>
                            {filterCriteria.value && (
                                <button
                                    className="lp-tool-btn"
                                    onClick={() => setFilterCriteria({ type: null, value: null })}
                                    style={{ color: '#0052CC', fontWeight: 'bold' }}
                                >
                                    Clear filter: {filterCriteria.value}
                                </button>
                            )}
                        </div>
                        <div className="lp-toolbar-right">
                            <button className="lp-tool-btn" onClick={handleExport}><Download size={14} /> Export <ChevronDown size={10} style={{ marginLeft: '4px' }} /></button>
                        </div>
                    </div>

                    <div className="timeline-view-content">
                        <div className="timeline-gantt-container">
                            <div className="timeline-months-header">
                                <div style={{ width: '250px', fontWeight: '700', color: '#172B4D', fontSize: '14px' }}>Task Description</div>
                                <div style={{ flex: 1, display: 'flex', justifyContent: 'space-around', color: '#6B778C', fontSize: '13px', fontWeight: '600' }}>
                                    <span>Feb 05</span>
                                    <span>Feb 10</span>
                                    <span>Feb 15</span>
                                    <span>Feb 20</span>
                                    <span>Feb 25</span>
                                </div>
                            </div>

                            {tasks.length > 0 ? tasks.map((task, idx) => {
                                const barColorClass = idx % 3 === 0 ? "timeline-bar-blue" : idx % 3 === 1 ? "timeline-bar-purple" : "timeline-bar-green";
                                return (
                                    <div key={idx} className="timeline-row">
                                        <div style={{ width: '250px', display: 'flex', alignItems: 'center', gap: '12px', fontSize: '14px', fontWeight: '500', color: '#172B4D' }}>
                                            <ChevronRight style={{ fontSize: '10px', color: '#6B778C', opacity: 0.5 }} size={10} />
                                            {task.title || task.task}
                                        </div>
                                        <div style={{ flex: 1, position: 'relative', height: '100%' }}>
                                            <div
                                                className={`timeline-bar ${barColorClass}`}
                                                style={{
                                                    position: 'absolute',
                                                    left: `${8 + (idx * 12) % 65}%`,
                                                    width: `${20 + (idx % 4) * 8}%`,
                                                    top: '12px',
                                                }}
                                            >
                                                <span style={{ marginLeft: '12px' }}>{task.status || 'Planned'}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }) : (
                                <div style={{ padding: '60px', textAlign: 'center', color: '#6B778C' }}>
                                    <CalendarIcon size={48} style={{ marginBottom: '16px', opacity: 0.2 }} />
                                    <p>No tasks found. Create some tasks to see them on the timeline.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Timeline;
